import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format as dateFnsFormat, isValid } from 'date-fns';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatSafe(date: string | number | Date | null | undefined, formatStr: string, fallback = '-'): string {
  if (!date) return fallback;
  const d = new Date(date);
  if (!isValid(d)) return fallback;
  try {
    return dateFnsFormat(d, formatStr);
  } catch (e) {
    return fallback;
  }
}

export function normalizeStatus(status: string | null | undefined): string {
  const s = (status || '').toLowerCase().trim();
  
  const clean = (val: string) => {
    return val.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  };
  const norm = clean(s);

  if (norm === '' || norm === 'null') return 'Novo Lead';

  if (['novo lead', 'novo'].includes(norm)) {
    return 'Novo Lead';
  }
  if (['em qualificacao', 'qualificação', 'qualificacao', 'qualificando', 'em qualificação'].includes(norm)) {
    return 'Em Qualificação';
  }
  if (['novo lead', 'novo'].includes(norm)) {
    return 'Novo Lead';
  }
  if (['em qualificacao', 'qualificacao'].includes(norm)) {
    return 'Em Qualificação';
  }
  if (['em atendimento', 'atendimento'].includes(norm)) {
    return 'Em Atendimento';
  }
  if (['visita agendada', 'agendado'].includes(norm)) {
    return 'Visita Agendada';
  }
  if (['proposta enviada', 'proposta'].includes(norm)) {
    return 'Proposta Enviada';
  }
  if (['em negociacao', 'negociacao'].includes(norm)) {
    return 'Em Negociação';
  }
  if (['ganho / venda fechada', 'ganho', 'venda fechada'].includes(norm)) {
    return 'Ganho / Venda Fechada';
  }
  if (['perdido / desqualificado', 'perdido', 'desqualificado'].includes(norm)) {
    return 'Perdido / Desqualificado';
  }
  if (['atencao humana', 'humano', 'precisa humano', 'atenção humana'].includes(norm)) {
    return 'Atenção Humana';
  }
  if (['atendimento encerrado', 'encerrado'].includes(norm)) {
    return 'Atendimento Encerrado';
  }
  if (['evento fechado', 'fechado', 'pago', 'concluido'].includes(norm)) {
    return 'Evento Fechado';
  }

  const officialStatuses = [
    'Novo Lead',
    'Em Qualificação',
    'Data Verificada',
    'Reunião Online Agendada',
    'Visita Presencial Agendada',
    'Degustação Agendada',
    'Pronto para Orçamento',
    'Validar Viabilidade',
    'Reserva Personalizada',
    'Buffet Externo',
    'Compromisso Cancelado',
    'Fora de Contexto / Desqualificado Leve',
    'Atenção Humana',
    'Atendimento Encerrado',
    'Evento Fechado'
  ];

  const matched = officialStatuses.find(title => clean(title) === norm);
  if (matched) return matched;

  return 'Novo Lead';
}

/**
 * Checks if a message is technical, internal or system-related.
 */
export function isTechnicalMessage(text: string | null | undefined, rawMessage?: any): boolean {
  if (rawMessage) {
    const senderType = rawMessage.message_sender_type || rawMessage.senderType;
    if (senderType === 'sistema') return true;

    const history = rawMessage.conversation_history;
    if (history) {
      const role = history.role;
      if (role === 'function' || role === 'system') return true;
    }

    // FIX (P7.2): Mensagens com mídia (image/audio/video/document/sticker/etc)
    // NUNCA são técnicas, mesmo se o content estiver vazio (legenda é opcional).
    // Antes deste fix, mensagens de imagem/áudio sem legenda eram filtradas pelo
    // isTechnicalMessage("") === true, escondendo a mídia do chat.
    const msgType = String(rawMessage.message_type || rawMessage.tipo || '').toLowerCase().trim();
    const mediaTypes = ['image', 'audio', 'video', 'document', 'sticker', 'ptt', 'voice', 'media', 'imagem', 'documento', 'pdf'];
    if (mediaTypes.includes(msgType)) {
      return false;
    }
    const mediaUrl = rawMessage.media_url || rawMessage.mediaUrl || rawMessage.url || rawMessage.arquivo_url;
    if (mediaUrl && typeof mediaUrl === 'string' && (mediaUrl.startsWith('http://') || mediaUrl.startsWith('https://') || mediaUrl.startsWith('data:'))) {
      return false;
    }
  }

  const str = String(text || '').trim();
  if (!str) return true;

  // Check if empty after cleanMetaText
  const cleaned = cleanMetaText(str);
  if (!cleaned) return true;

  // Se a mensagem for EXCLUSIVAMENTE um bloco [META], é técnica
  if (str.startsWith('[META') && str.endsWith(']') && !str.includes('\n')) {
    return true;
  }
  if (/^\[META[^\]]*\]$/i.test(str)) {
    return true;
  }

  const technicalPatterns = [
    '<function_calls',
    '</function_calls>',
    '<tool_code',
    '</tool_code>',
    'functionCall',
    'functionResponse',
    'verificar_horarios_disponiveis',
    'agendar_ligacao_especialista',
    'buscar_agendamentos_lead',
    'cancelar_agendamento_ligacao',
    'atualizar_dados_cadastrais',
    'pausar_atendimento'
  ];

  for (const pattern of technicalPatterns) {
    if (str.includes(pattern)) {
      return true;
    }
  }

  // Check if it looks like a raw JSON with function call metadata
  if (str.startsWith('{') && str.endsWith('}')) {
    try {
      const parsed = JSON.parse(str);
      if (parsed.functionCall || parsed.functionResponse || parsed.calls || parsed.tool_use || parsed.tool_code || parsed.tool_name) {
        return true;
      }
    } catch {
      // Ignored
    }
  }

  return false;
}

/**
 * Removes [META ...] tags from text, handling various positions and line breaks.
 */
export function cleanMetaText(text: string | null | undefined): string {
  if (!text) return '';
  
  // 1. Remove entire lines that contain [META
  // 2. Remove [META ...] tags that are in-line
  return text
    .split('\n')
    .filter(line => !line.trim().toUpperCase().includes('[META'))
    .join('\n')
    .replace(/\[META[^\]]*\]/gi, '')
    .trim();
}

/**
 * Normalizes and cleans message previews, removing [META] tags, XML tags, and bracket tags
 * and replacing them with a friendly text with emojis (like WhatsApp).
 */
export function getCleanedFriendlyPreview(text: string | null | undefined): string {
  if (!text) return '';
  
  let cleaned = cleanMetaText(text).trim();
  
  // Se for apenas uma string de placeholder entre colchetes
  const lower = cleaned.toLowerCase();
  if (lower === '[imagem]' || lower === '[image]') return '📷 Imagem';
  if (lower === '[áudio]' || lower === '[audio]') return '🎵 Áudio';
  if (lower === '[vídeo]' || lower === '[video]') return '🎥 Vídeo';
  if (lower === '[documento]' || lower === '[document]' || lower === '[pdf]') return '📄 Documento';
  if (lower === '[sticker]') return '🎨 Sticker';
  if (lower === '[mídia]' || lower === '[midia]') return '📁 Arquivo';

  // Se o texto contém tags XML de mídia, ex: <imagem>...</imagem>
  // Vamos substituir por emojis amigáveis
  cleaned = cleaned.replace(/<(imagem|image)>(.*?)<\/\1>/gi, (_, tag, content) => {
    const val = (content || '').trim();
    if (!val || val.startsWith('http://') || val.startsWith('https://')) {
      return '📷 Imagem';
    }
    return `📷 ${val}`;
  });

  cleaned = cleaned.replace(/<(audio|som)>(.*?)<\/\1>/gi, (_, tag, content) => {
    const val = (content || '').trim();
    if (!val || val.startsWith('http://') || val.startsWith('https://')) {
      return '🎵 Áudio';
    }
    return `🎵 ${val}`;
  });

  cleaned = cleaned.replace(/<(video)>(.*?)<\/\1>/gi, (_, tag, content) => {
    const val = (content || '').trim();
    if (!val || val.startsWith('http://') || val.startsWith('https://')) {
      return '🎥 Vídeo';
    }
    return `🎥 ${val}`;
  });

  cleaned = cleaned.replace(/<(pdf|documento|document)>(.*?)<\/\1>/gi, (_, tag, content) => {
    const val = (content || '').trim();
    if (!val || val.startsWith('http://') || val.startsWith('https://')) {
      return '📄 Documento';
    }
    return `📄 ${val}`;
  });

  // Caso ainda tenha colchetes feios soltos na mensagem, vamos limpá-los se forem placeholders isolados
  cleaned = cleaned.replace(/\[(imagem|image|áudio|audio|vídeo|video|documento|document|pdf|sticker|mídia|midia)\]/gi, (match, type) => {
    const t = type.toLowerCase();
    if (t === 'imagem' || t === 'image') return '📷 Imagem';
    if (t === 'áudio' || t === 'audio') return '🎵 Áudio';
    if (t === 'vídeo' || t === 'video') return '🎥 Vídeo';
    if (t === 'documento' || t === 'document' || t === 'pdf') return '📄 Documento';
    if (t === 'sticker') return '🎨 Sticker';
    return '📁 Arquivo';
  });

  return cleaned.trim();
}

export function safeText(value: unknown, fallback = ''): string {
  if (value === null || value === undefined) return fallback;
  return String(value);
}

export function safeLower(value: unknown): string {
  return safeText(value).toLowerCase();
}

export function safeTrim(value: unknown): string {
  return safeText(value).trim();
}

export function maskPhone(phone: string | undefined | null): string {
  if (!phone) return '';
  let s = String(phone).trim();
  // Remove qualquer caractere não-dígito
  s = s.replace(/\D/g, '');
  // Remove zeros à esquerda extras (algumas integrações enviam 0 + country)
  // Remove prefixos comuns de grupo
  if (s.startsWith('0')) s = s.replace(/^0+/, '');
  // Brasileiro: 13 dígitos (55 + DDD + 9 + 8) ou 12 (55 + DDD + 8) ou 11 (DDD + 9 + 8) ou 10 (DDD + 8)
  if (s.length === 13 && s.startsWith('55')) {
    return `+55 (${s.slice(2, 4)}) 9${s.slice(5, 9)}-${s.slice(9, 13)}`;
  }
  if (s.length === 12 && s.startsWith('55')) {
    return `+55 (${s.slice(2, 4)}) ${s.slice(4, 8)}-${s.slice(8, 12)}`;
  }
  if (s.length === 11) {
    return `(${s.slice(0, 2)}) ${s.slice(2, 7)}-${s.slice(7, 11)}`;
  }
  if (s.length === 10) {
    return `(${s.slice(0, 2)}) ${s.slice(2, 6)}-${s.slice(6, 10)}`;
  }
  // Fallback: retorna só dígitos
  return s;
}

export function formatRelativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  
  const diffMs = Date.now() - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'há 1 min';
  if (diffMin < 60) return `há ${diffMin} min`;
  if (diffHour < 24) return `há ${diffHour} h`;
  if (diffDay < 7) return `há ${diffDay} d`;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

export function normalizeMessageType(value: unknown): 'text' | 'audio' | 'image' | 'video' | 'document' {
  const v = safeLower(value).trim();

  if (!v || v === 'texto' || v === 'text' || v === 'conversation' || v === 'extendedtextmessage') return 'text';
  if (v === 'imagem' || v === 'image' || v === 'imagemessage') return 'image';
  if (v === 'audio' || v === 'audiomessage') return 'audio';
  if (v === 'video' || v === 'videomessage') return 'video';
  if (v === 'pdf' || v === 'documento' || v === 'document' || v === 'documentmessage') return 'document';

  return 'text';
}

export function normalizeForDedupe(value: unknown): string {
  return safeText(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/[.,;:!?]+$/g, '')
    .trim();
}

export function buildLeadUpdatePayload(localLeadData: any): any {
  const allowedKeys = [
    'nome',
    'telefone',
    'email',
    'remotejid',
    'whatsapp_pushname',
    'avatar_url',
    'etapa_id',
    'responsavel_user_id',
    'origem',
    'produto_interesse',
    'produto_id',
    'regiao',
    'cidade',
    'dados_qualificacao',
    'observacoes',
    'status_atendimento',
    'unread_count',
    'last_message_preview',
    'last_message_at',
    'last_message_sender_type',
    'proxima_visita_at',
    'atendimento_finalizado',
    'motivo_desqualificacao',
    'primeira_interacao_at',
    'ultima_interacao_at',
    'tags',
    'loja_id'
  ];
  const payload: any = {};
  for (const key of allowedKeys) {
    // Custom fields use their dedicated delta + revision contract, never a DTO replay.
    if (localLeadData && localLeadData[key] !== undefined) {
      payload[key] = localLeadData[key];
    }
  }
  return payload;
}

/** Moeda no formato pt-BR (fonte única; antes duplicada em CatalogCard e CatalogPicker). */
export const formatCurrency = (val: number, moeda = 'BRL'): string => {
  try {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: moeda || 'BRL',
    }).format(val || 0);
  } catch {
    return `R$ ${(val || 0).toFixed(2)}`;
  }
};
